import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import api from '@/lib/api';
import { STATUSES, PRIORITIES, PRIORITY_MAP, CATEGORIES, MANAGER_ROLES } from '@/lib/constants';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, ExternalLink, MessageSquare, ChevronLeft, ChevronRight, GitBranch, Calendar as CalIcon, Trash2, X, Send } from 'lucide-react';

const statusColors = {
  todo: '215 12% 55%',
  in_progress: '204 55% 38%',
  review: '28 78% 52%',
  done: '142 38% 34%',
};

function PriorityDot({ p }) {
  const info = PRIORITY_MAP[p] || PRIORITY_MAP.medium;
  return <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: `hsl(${info.color})` }} />;
}

function TaskCard({ task, onOpen, onMove, canManage }) {
  const info = PRIORITY_MAP[task.priority] || PRIORITY_MAP.medium;
  return (
    <div
      className="group rounded-lg border bg-card p-3 shadow-sm hover:shadow-md transition-shadow duration-150 cursor-pointer relative overflow-hidden"
      onClick={() => onOpen(task)}
      data-testid="task-card"
    >
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: `hsl(${info.color})` }} />
      <div className="flex items-start gap-2 pl-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium leading-snug line-clamp-2">{task.title}</div>
          {task.assignee_name && <div className="text-xs text-muted-foreground mt-1 truncate">👤 {task.assignee_name}</div>}
          <div className="flex flex-wrap gap-1.5 mt-2">
            <Badge variant="outline" className="text-[10px] font-medium" style={{ borderColor: `hsl(${info.color} / 0.35)`, color: `hsl(${info.color})` }}>
              <PriorityDot p={task.priority} /><span className="ml-1">{info.label}</span>
            </Badge>
            <Badge variant="secondary" className="text-[10px]">{task.category}</Badge>
            {task.deadline && <Badge variant="outline" className="text-[10px]"><CalIcon className="h-2.5 w-2.5 mr-1" />{task.deadline.slice(0, 10)}</Badge>}
          </div>
          {task.subtasks?.length ? (
            <div className="text-[11px] text-muted-foreground mt-2">
              {task.subtasks.filter((s) => s.done).length}/{task.subtasks.length} subtasks
            </div>
          ) : null}
          {task.github_urls?.length ? (
            <div className="flex gap-1 mt-1 flex-wrap">
              {task.github_urls.slice(0, 2).map((u) => (
                <span key={u} className="font-mono text-[10px] text-primary truncate max-w-[140px]"><GitBranch className="inline h-2.5 w-2.5 mr-0.5" />{u.split('/').slice(-2).join('/')}</span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-150" onClick={(e) => e.stopPropagation()}>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onMove(task, -1)} data-testid="task-card-move-left" aria-label="Move left"><ChevronLeft className="h-3 w-3" /></Button>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onMove(task, 1)} data-testid="task-card-move-right" aria-label="Move right"><ChevronRight className="h-3 w-3" /></Button>
        </div>
      </div>
    </div>
  );
}

function TaskDrawer({ open, onClose, task, onChanged, users, canManage }) {
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  if (!task) return null;

  const toggleSubtask = async (id) => {
    const updated = task.subtasks.map((s) => (s.id === id ? { ...s, done: !s.done } : s));
    try {
      const { data } = await api.patch(`/tasks/${task.task_id}`, { subtasks: updated });
      onChanged(data);
    } catch (e) { toast.error('Failed to update subtask'); }
  };

  const changeStatus = async (status) => {
    try {
      const { data } = await api.patch(`/tasks/${task.task_id}`, { status });
      onChanged(data);
      toast.success('Status updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Not allowed'); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/tasks/${task.task_id}/comments`, { body: comment });
      onChanged(data);
      setComment('');
    } catch (e) { toast.error('Failed to add comment'); } finally { setSaving(false); }
  };

  const addSubtask = async () => {
    const title = prompt('Subtask title?');
    if (!title) return;
    const updated = [...(task.subtasks || []), { id: 'st_' + Math.random().toString(36).slice(2, 10), title, done: false }];
    try {
      const { data } = await api.patch(`/tasks/${task.task_id}`, { subtasks: updated });
      onChanged(data);
    } catch { toast.error('Not allowed'); }
  };

  const addUrl = async () => {
    const url = prompt('GitHub/GitLab URL?');
    if (!url) return;
    const updated = [...(task.github_urls || []), url];
    try {
      const { data } = await api.patch(`/tasks/${task.task_id}`, { github_urls: updated });
      onChanged(data);
    } catch { toast.error('Not allowed'); }
  };

  return (
    <Drawer open={open} onOpenChange={(v) => !v && onClose()} direction="right">
      <DrawerContent className="max-w-lg ml-auto h-full flex flex-col" data-testid="task-detail-drawer">
        <DrawerHeader className="border-b">
          <div className="flex items-center justify-between">
            <DrawerTitle className="font-display text-lg pr-4" data-testid="task-detail-title">{task.title}</DrawerTitle>
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close"><X className="h-4 w-4" /></Button>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Select value={task.status} onValueChange={changeStatus}>
              <SelectTrigger className="w-40" data-testid="task-detail-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
            <Badge variant="outline">{task.category}</Badge>
            <Badge variant="outline">{PRIORITY_MAP[task.priority]?.label}</Badge>
          </div>
        </DrawerHeader>
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          <section>
            <h3 className="text-sm font-semibold mb-1">Description</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{task.description || 'No description.'}</p>
            <div className="mt-3 text-xs text-muted-foreground space-y-1">
              <div>Assignee: <span className="text-foreground">{task.assignee_name || 'Unassigned'}</span></div>
              <div>Created by: {task.created_by_name || task.created_by}</div>
              {task.deadline && <div>Deadline: {task.deadline.slice(0, 10)}</div>}
            </div>
          </section>

          <section data-testid="task-detail-subtasks">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">Subtasks</h3>
              <Button size="sm" variant="ghost" onClick={addSubtask}><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
            </div>
            {task.subtasks?.length ? (
              <ul className="space-y-2">{task.subtasks.map((s) => (
                <li key={s.id} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={s.done} onCheckedChange={() => toggleSubtask(s.id)} data-testid="subtask-checkbox" />
                  <span className={s.done ? 'line-through text-muted-foreground' : ''}>{s.title}</span>
                </li>
              ))}</ul>
            ) : <div className="text-xs text-muted-foreground">No subtasks.</div>}
          </section>

          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">GitHub / GitLab links</h3>
              <Button size="sm" variant="ghost" onClick={addUrl}><Plus className="h-3.5 w-3.5 mr-1" />Add URL</Button>
            </div>
            {task.github_urls?.length ? (
              <div className="flex flex-col gap-1">{task.github_urls.map((u) => (
                <a key={u} href={u} target="_blank" rel="noreferrer" className="font-mono text-xs text-primary hover:underline flex items-center gap-1" data-testid="task-detail-github-chip">
                  <ExternalLink className="h-3 w-3" />{u}
                </a>
              ))}</div>
            ) : <div className="text-xs text-muted-foreground">No links yet.</div>}
          </section>

          <section data-testid="task-detail-comments">
            <h3 className="text-sm font-semibold mb-2">Comments</h3>
            <div className="space-y-3">
              {task.comments?.length ? task.comments.map((c) => (
                <div key={c.id} className="border rounded-md p-2 bg-secondary/40">
                  <div className="text-xs font-medium">{c.author_name} <span className="text-muted-foreground font-normal">• {new Date(c.created_at).toLocaleString()}</span></div>
                  <div className="text-sm mt-1 whitespace-pre-wrap">{c.body}</div>
                </div>
              )) : <div className="text-xs text-muted-foreground">No comments yet.</div>}
            </div>
          </section>
        </div>
        <div className="border-t p-3 flex gap-2 items-end">
          <Textarea placeholder="Add a comment or blocker…" value={comment} onChange={(e) => setComment(e.target.value)} className="min-h-[60px]" data-testid="comment-input" />
          <Button onClick={addComment} disabled={saving || !comment.trim()} data-testid="task-detail-add-comment-button"><Send className="h-4 w-4" /></Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function NewTaskDialog({ users, onCreated, canManage }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium', category: 'general', assignee_id: '', deadline: '' });
  const [saving, setSaving] = useState(false);

  if (!canManage) return null;

  const submit = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.assignee_id) delete body.assignee_id;
      if (!body.deadline) delete body.deadline;
      await api.post('/tasks', body);
      toast.success('Task created');
      setOpen(false);
      setForm({ title: '', description: '', priority: 'medium', category: 'general', assignee_id: '', deadline: '' });
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create'); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-task-button"><Plus className="h-4 w-4 mr-1" />New Task</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="new-task-dialog">
        <DialogHeader><DialogTitle className="font-display">Create task</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="new-task-title" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="new-task-desc" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Priority</Label><Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger data-testid="new-task-priority"><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Category</Label><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid="new-task-category"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Assignee</Label><Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}><SelectTrigger data-testid="new-task-assignee"><SelectValue placeholder="Choose…" /></SelectTrigger><SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Deadline</Label><Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="new-task-deadline" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.title.trim()} data-testid="new-task-submit">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Tasks() {
  const { user } = useAuth();
  const canManage = MANAGER_ROLES.has(user?.role);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [view, setView] = useState('kanban');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, u] = await Promise.all([api.get('/tasks'), api.get('/users')]);
      setTasks(t.data);
      setUsers(u.data);
    } catch (e) { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => tasks.filter((t) => (
    (filterAssignee === 'all' || t.assignee_id === filterAssignee) &&
    (filterPriority === 'all' || t.priority === filterPriority)
  )), [tasks, filterAssignee, filterPriority]);

  const byStatus = useMemo(() => {
    const map = { todo: [], in_progress: [], review: [], done: [] };
    filtered.forEach((t) => (map[t.status] || map.todo).push(t));
    return map;
  }, [filtered]);

  const moveTask = async (task, direction) => {
    const idx = STATUSES.findIndex((s) => s.value === task.status);
    const next = STATUSES[Math.max(0, Math.min(STATUSES.length - 1, idx + direction))].value;
    if (next === task.status) return;
    try {
      const { data } = await api.patch(`/tasks/${task.task_id}`, { status: next });
      setTasks((prev) => prev.map((t) => t.task_id === data.task_id ? data : t));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Not allowed'); }
  };

  const handleChanged = (updated) => {
    setTasks((prev) => prev.map((t) => t.task_id === updated.task_id ? updated : t));
    setSelected(updated);
  };

  return (
    <AppShell title="Tasks" actions={<NewTaskDialog users={users} onCreated={load} canManage={canManage} />}>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <Tabs value={view} onValueChange={setView} data-testid="tasks-view-toggle">
            <TabsList>
              <TabsTrigger value="kanban" data-testid="view-kanban">Kanban</TabsTrigger>
              <TabsTrigger value="list" data-testid="view-list">List</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="ml-auto flex gap-2">
            <Select value={filterAssignee} onValueChange={setFilterAssignee}>
              <SelectTrigger className="w-44" data-testid="tasks-filter-assignee"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All assignees</SelectItem>
                {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-36" data-testid="tasks-filter-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="loading-skeleton">
            {STATUSES.map((s) => (
              <div key={s.value} className="space-y-2">
                <Skeleton className="h-6 w-20" />
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
              </div>
            ))}
          </div>
        ) : view === 'kanban' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="tasks-kanban-board">
            {STATUSES.map((s) => (
              <div key={s.value} className="rounded-xl border bg-secondary/40 p-3 min-w-[260px]" data-testid="tasks-kanban-column">
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: `hsl(${statusColors[s.value]})` }} />
                    <div className="font-medium text-sm">{s.label}</div>
                    <Badge variant="secondary" className="text-[10px]">{byStatus[s.value].length}</Badge>
                  </div>
                </div>
                <div className="space-y-2 max-h-[calc(100vh-260px)] overflow-y-auto pr-1">
                  {byStatus[s.value].map((t) => (
                    <TaskCard key={t.task_id} task={t} onOpen={setSelected} onMove={moveTask} canManage={canManage} />
                  ))}
                  {byStatus[s.value].length === 0 && (
                    <div className="text-xs text-muted-foreground text-center py-6" data-testid="empty-state">No tasks.</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card className="overflow-x-auto" data-testid="tasks-list-view">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60">
                <tr>
                  <th className="text-left p-3">Title</th>
                  <th className="text-left p-3">Assignee</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Priority</th>
                  <th className="text-left p-3">Deadline</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.task_id} className="border-t hover:bg-accent/40 cursor-pointer" onClick={() => setSelected(t)} data-testid="task-row">
                    <td className="p-3 font-medium">{t.title}</td>
                    <td className="p-3">{t.assignee_name || '—'}</td>
                    <td className="p-3">{STATUSES.find((s) => s.value === t.status)?.label}</td>
                    <td className="p-3"><Badge variant="outline"><PriorityDot p={t.priority} /><span className="ml-1">{PRIORITY_MAP[t.priority]?.label}</span></Badge></td>
                    <td className="p-3">{t.deadline?.slice(0, 10) || '—'}</td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground" data-testid="empty-state">No tasks match your filters.</td></tr>}
              </tbody>
            </table>
          </Card>
        )}

        <TaskDrawer open={!!selected} onClose={() => setSelected(null)} task={selected} onChanged={handleChanged} users={users} canManage={canManage} />
      </div>
    </AppShell>
  );
}
